import { clsx } from "clsx";
import { Trans, useTranslation } from "react-i18next";
import { Btn } from "@/ui/btn/Btn";
import { CheckboxCircle } from "@/ui/checkbox-circle/CheckboxCircle";
import { Icon } from "@/ui/icon/Icon";
import type { Icons } from "@/ui/icon/icons";
import { Img } from "@/ui/img/Img";
import { TextInterpolate } from "@/ui/text-interpolate/TextInterpolate";
import { Tooltip } from "@/ui/tooltip/Tooltip";
import { gtmEvent } from "@/utils/google";
import { Network } from "@/utils/network";
import { formatCompact, formatPretty } from "@/utils/number";
import { blurSensitiveData } from "@/utils/privacy";
import { useInstagramSearch } from "../../hooks/useInstagramSearch";
import { useInstagramSearchStorage } from "../../hooks/useInstagramSearchStorage";
import type { InstagramSearch } from "../../types";
import { requestInstagramSearchAuth } from "../../utils/utils";
import { InstagramSearchListsAssignMenu } from "../instagram-search-lists-assign-menu/InstagramSearchListsAssignMenu";
import styles from "./InstagramSearchProfile.module.css";

interface InstagramSearchProfileProps {
  profile: InstagramSearch.Profile;
}

export const InstagramSearchProfile: React.FC<InstagramSearchProfileProps> = ({
  profile,
}) => {
  const { t } = useTranslation();
  const config = useInstagramSearch((state) => state.config);
  const selection = useInstagramSearch((state) => state.selection);
  const toggleSelected = useInstagramSearch((state) => state.toggleSelected);
  const isSubscribed = useInstagramSearch((state) => state.isSubscribed);
  const layout = useInstagramSearchStorage((state) => state.layout);

  return (
    <div
      className={clsx(
        styles.profile,
        selection.has(profile.id) && styles.selected,
        styles[layout],
        profile.limit !== undefined && styles.blur,
      )}
    >
      {profile.limit === undefined && (
        <CheckboxCircle
          className={styles.checkbox}
          checked={selection.has(profile.id)}
          onChange={() => toggleSelected(new Set([profile.id]))}
        />
      )}
      <div className={styles.content}>
        <div className={clsx(styles.stats, styles.posts)}>
          <div className={styles.value}>{formatCompact(profile.posts)}</div>
          <div className={styles.title}>{t("profile.posts")}</div>
        </div>

        <Img
          className={styles.image}
          src={profile.image}
          width={68}
          height={68}
        />

        <div className={clsx(styles.stats, styles.followers)}>
          <div className={styles.value}>{formatCompact(profile.followers)}</div>
          <div className={styles.title}>{t("profile.followers")}</div>
        </div>

        <div className={styles.info}>
          <a
            className={styles.username}
            href={Network.url(config.links.profileAnalyzer, {
              profile: profile.username,
            }).toString()}
            target="_blank"
            onClick={() => {
              gtmEvent("search", "search_click_username", "submit");
            }}
            data-event-section="search-result"
            data-event-source="open-analyzer"
          >
            <Icon
              className={styles.icon}
              name="instagram_20px_bold"
              size={20}
            />
            <div className={styles.value}>{profile.username}</div>
          </a>
          <div className={styles.name}>{profile.name}</div>
          <div className={styles.controls}>
            <Btn
              href={Network.url(config.links.instagramViewer, {
                profile: profile.username,
              }).toString()}
              text={t("profile.viewer")}
              color="stroke"
              size="sm"
              target="_blank"
              onClick={() => {
                gtmEvent("search", "search_click_viewer", "submit");
              }}
              data-event-section="search-result"
              data-event-source="open-viewer"
            />
            <Btn
              href={Network.url(config.links.instagramDownloader, {
                profile: profile.username,
              }).toString()}
              text={t("profile.downloader")}
              color="stroke"
              size="sm"
              target="_blank"
              data-event-section="search-result"
              data-event-source="open-downloader"
            />
            <Btn
              href={Network.url(config.links.profileAnalyzer, {
                profile: profile.username,
              }).toString()}
              text={t("profile.analytics")}
              color="stroke"
              size="sm"
              target="_blank"
              onClick={() => {
                gtmEvent("search", "search_click_analytics", "submit");
              }}
              data-event-section="search-result"
              data-event-source="open-analyzer"
            />
            <InstagramSearchListsAssignMenu
              type="toggle"
              text={t("profile.addToList")}
              color="stroke"
              size="sm"
              profile={profile}
            />
          </div>
        </div>

        {(profile.email !== undefined ||
          profile.phone !== undefined ||
          profile.website !== undefined) && (
          <div className={styles.contacts}>
            {profile.email !== undefined && (
              <InstagramSearchProfileContact
                icon="mail_16px"
                value={profile.email}
                protocol="mailto:"
              />
            )}
            {profile.phone !== undefined && (
              <InstagramSearchProfileContact
                icon="phone_16px"
                value={profile.phone}
                protocol="tel:"
              />
            )}
            {profile.website !== undefined && (
              <InstagramSearchProfileContact
                icon="link_16px"
                value={profile.website}
              />
            )}
          </div>
        )}

        <div className={styles.text}>
          <TextInterpolate
            text={isSubscribed ? profile.text : blurSensitiveData(profile.text)}
            components={{
              blur: <span className={styles.blur} />,
            }}
          />
        </div>
      </div>

      {profile.limit !== undefined && (
        <div className={styles.limits}>
          <div className={styles["limits-title"]}>
            {t("profile.limits.title", {
              count: profile.limit.count,
              total: formatPretty(profile.limit.total),
            })}
          </div>
          {profile.limit.type === "sign-up" && (
            <>
              <div className={styles["limits-text"]}>
                <Trans
                  i18nKey="profile.limits.signUpText"
                  components={{
                    a: (
                      <a
                        className={styles["limits-link"]}
                        href={profile.limit.href}
                        onClick={(event) => {
                          event.preventDefault();
                          requestInstagramSearchAuth();
                        }}
                        data-event-section="search-limit"
                        data-event-source="sign-up"
                      />
                    ),
                  }}
                />
              </div>
              <Btn
                href={profile.limit.href}
                icon="go_24px_filled"
                text={t("profile.limits.signUpBtnText")}
                color="primary"
                onClick={(event) => {
                  event.preventDefault();
                  requestInstagramSearchAuth();
                }}
                data-event-section="search-limit"
                data-event-source="sign-up"
              />
            </>
          )}
          {profile.limit.type === "subscribe" && (
            <>
              <div className={styles["limits-text"]}>
                <Trans
                  i18nKey="profile.limits.subscribeText"
                  components={{
                    a: (
                      <a
                        className={styles["limits-link"]}
                        href={profile.limit.href}
                        data-event-section="search-limit"
                        data-event-source="subscribe"
                      />
                    ),
                  }}
                />
              </div>
              <Btn
                href={profile.limit.href}
                icon="go_24px_filled"
                text={t("profile.limits.subscribeBtnText")}
                color="primary"
                data-event-section="search-limit"
                data-event-source="subscribe"
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

interface InstagramSearchProfileContactEmailProps {
  icon: keyof Icons;
  value: string;
  protocol?: "tel:" | "mailto:";
}

const InstagramSearchProfileContact: React.FC<
  InstagramSearchProfileContactEmailProps
> = ({ icon, value, protocol = "" }) => {
  const { t } = useTranslation();
  const config = useInstagramSearch((state) => state.config);
  const isAuthorized = useInstagramSearch((state) => state.isAuthorized);
  const isSubscribed = useInstagramSearch((state) => state.isSubscribed);

  if (isAuthorized === false) {
    return (
      <div className={clsx(styles.contact, styles.locked)}>
        <Icon className={styles.icon} name={icon} size={16} />
        <div className={styles.value}>{"*".repeat(value.length)}</div>
        <Tooltip interactive={false}>
          <Tooltip.Toggle>
            <a
              className={styles.lock}
              href={window.authConfig?.links.signUp}
              onClick={(event) => {
                event.preventDefault();

                requestInstagramSearchAuth();

                gtmEvent("search", "search_click_sign_up", "submit");
              }}
              data-event-section="search-contacts"
              data-event-source="sign-up"
            >
              <Icon
                className={styles.icon}
                name="access_16px_filled"
                size={16}
              />
            </a>
          </Tooltip.Toggle>
          <Tooltip.Popup>
            <Tooltip.Group>
              <Tooltip.Text>{t("profile.signUpToUnlock")}</Tooltip.Text>
              <Icon className={styles.icon} name="go_16px" size={16} />
            </Tooltip.Group>
          </Tooltip.Popup>
        </Tooltip>
      </div>
    );
  }

  if (isSubscribed === false) {
    return (
      <div className={clsx(styles.contact, styles.locked)}>
        <Icon className={styles.icon} name={icon} size={16} />
        <div className={styles.value}>{"*".repeat(value.length)}</div>
        <Tooltip interactive={false}>
          <Tooltip.Toggle>
            <a
              className={styles.lock}
              href={config.links.instagramSearchSubscribe}
              onClick={() => {
                gtmEvent("search", "search_click_purchase", "submit");
              }}
              data-event-section="search-contacts"
              data-event-source="subscribe"
            >
              <Icon
                className={styles.icon}
                name="access_16px_filled"
                size={16}
              />
            </a>
          </Tooltip.Toggle>
          <Tooltip.Popup>
            <Tooltip.Group>
              <Tooltip.Text>{t("profile.subscribeToUnlock")}</Tooltip.Text>
              <Icon className={styles.icon} name="go_16px" size={16} />
            </Tooltip.Group>
          </Tooltip.Popup>
        </Tooltip>
      </div>
    );
  }

  return (
    <a
      className={styles.contact}
      href={`${protocol}${value}`}
      target="_blank"
      rel="nofollow"
    >
      <Icon className={styles.icon} name={icon} size={16} />
      <div className={styles.value}>{value}</div>
    </a>
  );
};
